// Phase 48d: Dashboard widget for savings goals.
//
// Shows the top-3 ACTIVE in-progress goals sorted by pct-desc (see
// sortGoalsByProgress in lib/goals.js). Hidden entirely when no goals
// exist — the Settings page carries the onboarding, not the Dashboard.
// Reuses BudgetProgressBar (Phase 48b refactor) so progress bars look
// identical across Dashboard BudgetAlerts, Budgets page, and Goals.
//
// Phase 48d mental model: every active goal tracks its share of the
// user's monthly net savings flow (SUM(income) - SUM(expense) since
// tracking_from) based on its allocation_pct. ETA = avg of last 3
// months' share. We also display the projected completion month
// (eta_date_iso) alongside the relative ETA ("預計 2027-04 達標").

import React from 'react';
import { Link } from 'react-router-dom';
import BudgetProgressBar from './BudgetProgressBar.jsx';
import { useI18n } from '../i18n/index.jsx';
import { computeGoalProgress, sortGoalsByProgress } from '../lib/goals.js';
import { formatMoney, formatPercent } from '../lib/format.js';

const TOP_N = 3;

export default function GoalsSummary({ goals = [], transactions = [], currency = '$' }) {
  const { t } = useI18n();

  if (!goals || goals.length === 0) return null;

  // Decorate each goal with progress (each goal computes its share
  // independently — no sibling denominator needed in Phase 48e).
  const decorated = sortGoalsByProgress(
    goals.map(g => ({ goal: g, progress: computeGoalProgress(g, transactions) }))
  )
    // Only show ACTIVE goals in the widget. Inactive goals still appear
    // in Settings (paused, frozen) but shouldn't compete for screen.
    .filter(({ goal }) => goal.active !== false)
    .slice(0, TOP_N);

  if (decorated.length === 0) return null;

  const activeCount = goals.filter(g => g.active !== false).length;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3 sm:p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
          <span aria-hidden="true">🎯</span>
          <span>{t('goals.widgetTitle', { n: activeCount })}</span>
        </h2>
        <Link
          to="/settings#savings-goals"
          className="text-xs text-brand-600 dark:text-brand-400 hover:underline min-h-[44px] inline-flex items-center"
        >
          {t('common.edit')}
        </Link>
      </div>

      <ul className="space-y-3">
        {decorated.map(({ goal, progress }) => {
          const isDone = progress.current_amount >= goal.target_amount;
          // Cap visual pct at 100% so the bar + label don't overflow
          // when a goal is exceeded (e.g. 300% → display "100%"). The
          // actual current_amount/target is still shown verbatim below.
          const displayPct = Math.min(progress.pct, 1);
          const overAmount = Math.max(0, progress.current_amount - goal.target_amount);
          return (
            <li key={goal.local_uuid} className="flex items-start gap-3">
              <span
                className="w-9 h-9 rounded-full flex items-center justify-center text-base shrink-0"
                style={{ backgroundColor: goal.color + '20' }}
                aria-hidden="true"
              >
                {goal.icon}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="font-medium text-sm text-slate-900 dark:text-slate-100 truncate">
                    {goal.name}
                  </span>
                  {isDone ? (
                    // Done: show "✓ 達標" in emerald (success), not 300%.
                    <span className="text-xs font-medium tabular-nums shrink-0 text-emerald-600 dark:text-emerald-400">
                      ✓
                    </span>
                  ) : (
                    <span className={`text-xs tabular-nums shrink-0 ${
                      displayPct >= 0.8 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400'
                    }`}>
                      {formatPercent(progress.pct, 0)}
                    </span>
                  )}
                </div>
                <BudgetProgressBar
                  pct={displayPct}
                  variant="goal"
                  ariaLabel={t('goals.progressAria', { name: goal.name, pct: formatPercent(progress.pct, 0) })}
                />
                <div className="flex items-center justify-between mt-1 text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                  <span>
                    {formatMoney(progress.current_amount, goal.currency || currency)}
                    <span className="text-slate-400 dark:text-slate-500"> / {formatMoney(goal.target_amount, goal.currency || currency)}</span>
                  </span>
                  {isDone ? (
                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                      {overAmount > 0
                        ? t('goals.completedOver', { amount: formatMoney(overAmount, goal.currency || currency) })
                        : t('goals.completed')}
                    </span>
                  ) : progress.eta_date_iso != null ? (
                    // Projected completion month — primary label.
                    <span>{t('goals.etaDate', { date: progress.eta_date_iso })}</span>
                  ) : (
                    <span>{t('goals.etaNoHistory')}</span>
                  )}
                </div>
                {progress.eta_months != null && !isDone && progress.allocation_pct > 0 && (
                  <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                    {t('goals.allocationHint', {
                      pct: progress.allocation_pct,
                      months: progress.eta_months,
                    })}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}